import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAssignedJiraIssues, getJiraCredentials } from '../../services/jiraService';

const statusTone = (category) => {
  const key = String(category || '').toLowerCase();
  if (key.includes('done')) return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
  if (key.includes('progress') || key.includes('indeterminate')) {
    return 'bg-sky-50 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200';
  }
  return 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200';
};

const JiraAssignedTicketsList = ({ compact = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [issues, setIssues] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const credentials = await getJiraCredentials();
      if (!credentials?.isConnected) {
        setConnected(false);
        setIssues([]);
        return;
      }
      setConnected(true);
      setIssues(await fetchAssignedJiraIssues());
    } catch (err) {
      const message = err?.message || '';
      if (message.toLowerCase().includes('not connected')) {
        setConnected(false);
        setIssues([]);
      } else {
        setError(message || 'Failed to load Jira tickets');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <p className="px-4 py-8 text-center text-sm text-gray-500">Loading Jira tickets…</p>;
  }

  if (!connected) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Jira is not connected</p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Connect Jira to see tickets assigned to you.
        </p>
        <button
          type="button"
          onClick={() => navigate('/integrations?plugin=jira')}
          className="mt-3 px-3 py-1.5 text-sm font-semibold rounded-lg text-white bg-primary-600 hover:bg-primary-700"
        >
          Connect Jira
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
        <button type="button" onClick={load} className="mt-2 text-sm font-semibold text-primary-600">
          Retry
        </button>
      </div>
    );
  }

  if (!issues.length) {
    return <p className="px-4 py-8 text-center text-sm text-gray-500">No open Jira tickets assigned to you</p>;
  }

  return (
    <div className={compact ? 'divide-y divide-gray-100 dark:divide-gray-700' : 'space-y-2 p-2'}>
      {issues.map((issue) => (
        <a
          key={issue.id || issue.key}
          href={issue.browseUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-2.5 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40"
        >
          <span className="mt-0.5 inline-flex h-7 min-w-[3.25rem] items-center justify-center rounded-md bg-indigo-50 px-1.5 text-[10px] font-bold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
            {issue.key}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-gray-900 dark:text-white leading-snug">
              {issue.summary || issue.key}
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
              {issue.status ? (
                <span className={`px-1.5 py-0.5 rounded ${statusTone(issue.statusCategory)}`}>
                  {issue.status}
                </span>
              ) : null}
              {issue.issueType ? <span>{issue.issueType}</span> : null}
              {issue.priority ? <span>· {issue.priority}</span> : null}
              {issue.projectKey ? <span>· {issue.projectKey}</span> : null}
            </span>
          </span>
        </a>
      ))}
    </div>
  );
};

export default JiraAssignedTicketsList;
